import { Divider, Dropdown, IconButton, Menu, MenuButton, MenuItem } from '@mui/joy';
import { useContext, useMemo } from 'react';
import { lowerFirst } from 'lodash';
import { useTranslation } from 'react-i18next';

import { actions } from '@/reducers/ui';
import { AppContext } from '@/shared/context/context';
import useToggle from '../../../shared/hooks/UseToggle';
import { useUserRole } from '@/queries';

import {
  AccessKeyIcon,
  AddIcon,
  BuildingIcon,
  CaseIcon,
  CashRegisterIcon,
  CompanyIcon,
  ContactIcon,
  InsurancePolicyIcon,
  InsuredCaseIcon,
  RepairCaseIcon,
  SalesCaseIcon,
  TeamIcon,
} from '../../../shared/icons/Icons';
import AddAccessKeyActionFormModal from '@/containers/AccessKeys/actions/components/AddAccessKeyActionFormModal';
import AddCase from '@/containers/Cases/AddEditCase/AddCase';
import AddCompanyModal from '../../Companies/AddEditCompany/AddEditCompanyModal';
import AddEditBuildingModal from '../../Buildings/AddEditBuilding/AddEditBuildingModal';
import AddEditContactModal from '../../Contacts/AddEditContact/AddEditContactModal';
import AddEditPerformanceModal from '@/containers/HomeownerAssociation/Performances/AddEditPerformanceModal';
import AddInsuranceCaseModal from '../../Cases/AddInsuranceCase/AddInsuranceCaseModal';
import AddPolicyModal from '../../Policies/AddEditPolicy/AddPolicyModal';
import AddRepairCaseStepper from '@/containers/Cases/AddEditCase/repairCaseStepper/AddRepairCaseStepper';
import AddSalesCase from '@/containers/Cases/AddSalesCase/AddSalesCase';
import AddTeamModal from '@/containers/Teams/AddEditTeam/AddTeamModal';
import KpcTooltip from '@/shared/components/Tooltips/KpcTooltip';
import { trpc } from '@/config/trpc';

const TopbarQuickAction = () => {
  const { ui, setUi, currentTeam } = useContext(AppContext);
  const { t } = useTranslation();
  const { isSupervisor, isAdmin } = useUserRole();

  const toggles = {
    addBuilding: useToggle(),
    addCase: useToggle(),
    addCompany: useToggle(),
    addContact: useToggle(),
    addInsurance: useToggle(),
    addSalesCase: useToggle(),
    addTeam: useToggle(),
    chargePerformance: useToggle(),
    createPolicy: useToggle(),
    registerAccessKeyAction: useToggle(),
  };

  const { data: repairCompanies } = trpc.company.repairCompanies.useQuery();
  const requestRepairCasePossible = useMemo(
    () => !!repairCompanies?.filter((team) => team.repairInKindPossible).length,
    [repairCompanies],
  );
  const noRepairPossibleReason = useMemo(
    () =>
      repairCompanies
        ?.filter((team) => !!team.noRepairInKindReason)
        .map((team) => `${team.name}: ${lowerFirst(team.noRepairInKindReason ?? '')}`)
        .join('; '),
    [repairCompanies],
  );

  const currentTeamCannotCreateRepairCase =
    !currentTeam?.allowRepairManagement && !requestRepairCasePossible;

  return (
    <>
      <Dropdown>
        <MenuButton
          className="rounded-full"
          slots={{ root: IconButton }}
          slotProps={{ root: { variant: 'plain' } }}
        >
          <AddIcon />
        </MenuButton>

        <Menu placement="bottom-end">
          {currentTeam && currentTeam.allowCaseManagement && (
            <>
              <MenuItem className="px-4 py-2" onClick={toggles.addCase.show}>
                <i className="w-8">
                  <CaseIcon />
                </i>
                {t('_caseTypes.case')}
              </MenuItem>

              <KpcTooltip
                title={currentTeamCannotCreateRepairCase ? noRepairPossibleReason : ''}
                placement="right"
              >
                <MenuItem
                  className="px-4 py-2"
                  disabled={currentTeamCannotCreateRepairCase ?? false}
                  onClick={() => setUi({ type: actions.CASE_TOGGLE_CREATE_REPAIR_CASE })}
                >
                  <i className="w-8">
                    <RepairCaseIcon />
                  </i>
                  {t('_caseTypes.repair')}
                </MenuItem>
              </KpcTooltip>

              <MenuItem className="px-4 py-2" onClick={toggles.addInsurance.show}>
                <i className="w-8">
                  <InsuredCaseIcon />
                </i>
                {t('_caseTypes.insuranceClaim')}
              </MenuItem>

              {currentTeam.allowSyndicManagement && (
                <MenuItem className="px-4 py-2" onClick={toggles.addSalesCase.show}>
                  <i className="w-8">
                    <SalesCaseIcon />
                  </i>
                  {t('_caseTypes.sales')}
                </MenuItem>
              )}
              <Divider />
            </>
          )}
          <Divider />
          <MenuItem className="px-4 py-2" onClick={toggles.addContact.show}>
            <i className="w-8">
              <ContactIcon />
            </i>
            {t('_contact.title')}
          </MenuItem>
          <MenuItem className="px-4 py-2" onClick={toggles.addCompany.show}>
            <i className="w-8">
              <CompanyIcon />
            </i>
            {t('_company.title')}
          </MenuItem>
          <MenuItem className="px-4 py-2" onClick={toggles.addBuilding.show}>
            <i className="w-8">
              <BuildingIcon />
            </i>
            {t('building_one')}
          </MenuItem>
          <MenuItem className="px-4 py-2" onClick={toggles.createPolicy.show}>
            <i className="w-8">
              <InsurancePolicyIcon />
            </i>
            {t('policy')}
          </MenuItem>
          {currentTeam && currentTeam.allowSyndicManagement && (
            <>
              <Divider />
              <MenuItem className="px-4 py-2" onClick={toggles.chargePerformance.show}>
                <i className="w-8">
                  <CashRegisterIcon />
                </i>
                {t('chargeAPerformance')}
              </MenuItem>
            </>
          )}
          {currentTeam && currentTeam.allowAccessKeyManagement && (
            <>
              <Divider />
              <MenuItem className="px-4 py-2" onClick={toggles.registerAccessKeyAction.show}>
                <i className="w-8">
                  <AccessKeyIcon />
                </i>
                {t('_accessKeyActions.registerAction')}
              </MenuItem>
            </>
          )}
          {isSupervisor && <Divider />}
          {isAdmin && (
            <MenuItem className="px-4 py-2" onClick={toggles.addTeam.show}>
              <i className="w-8">
                <TeamIcon />
              </i>
              {t('addTypes', { type: t('team') })}
            </MenuItem>
          )}
        </Menu>
      </Dropdown>
      {toggles.addCase.value && <AddCase toggle={toggles.addCase} />}
      {ui.case.toggles.createRepairCase && <AddRepairCaseStepper />}
      {toggles.addInsurance.value && <AddInsuranceCaseModal toggle={toggles.addInsurance} />}
      {toggles.addSalesCase.value && <AddSalesCase toggle={toggles.addSalesCase} />}

      <AddEditContactModal toggle={toggles.addContact} />
      <AddCompanyModal toggle={toggles.addCompany} />
      {toggles.addBuilding.value && <AddEditBuildingModal toggle={toggles.addBuilding} />}
      <AddPolicyModal toggle={toggles.createPolicy} />

      <AddEditPerformanceModal toggle={toggles.chargePerformance} />

      {toggles.registerAccessKeyAction.value && (
        <AddAccessKeyActionFormModal toggle={toggles.registerAccessKeyAction} />
      )}

      {isSupervisor && <AddTeamModal toggle={toggles.addTeam} />}
    </>
  );
};

export default TopbarQuickAction;
